import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClipboardCheck,
  Send,
  Lock,
  Unlock,
  Loader2,
  Trash2,
  RefreshCw,
  FileSpreadsheet,
  FileDown,
  CheckCircle2,
  UserRoundSearch,
  X,
  XCircle,
} from "lucide-react";
import Select, { components } from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import HrmDashboardBranchPicker, {
  buildHrmDashboardBranchSelectOptions,
} from "../shared/hrmDashboardBranchSelect";
import AttendanceMonthSheetView, {
  workDateKey,
  formatYmdKeyVN,
  buildMonthSheetRows,
  mapApiExplanationToDraft,
  emptyDraftExplanation,
} from "./AttendanceMonthSheetView";
import { parseMonthKey, buildPivotAoA } from "./attendanceExportPivot";
import { downloadPayrollConfirmationExcel } from "./attendanceExportExcel";
import { formatDateTime } from "../../../utils/formatUtils";
import { hasPermission } from "../../../utils/permissionUtils";
import {
  showConfirmDialog,
  showInputDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import {
  getAttendanceConfirmationPeriod,
  getMyAttendanceConfirmationOverview,
  getAttendanceConfirmationEmployees,
  getAttendanceConfirmationEmployeeOverview,
  postAttendanceConfirmationEmployeeReview,
  postAttendanceHourExplanation,
  postAttendanceExplanationReview,
  deleteAttendanceHourExplanation,
  postAcknowledgeAttendancePayroll,
  deleteAttendancePayrollAcknowledgment,
  getAttendancePayrollAgreements,
  postAttendanceConfirmationRequest,
  postAttendanceConfirmationClose,
  postAttendanceConfirmationReopenPeriod,
  getAttendanceConfirmationExplanations,
  getAttendanceConfirmationPayrollExportData,
  getAttendanceConfirmationClosedPeriodReport,
} from "../../../api/hrm/employees/attendanceConfirmation";
import { downloadClosedPeriodReportPdf } from "./attendanceClosedPeriodReportPdf";

function buildExplanationDraftMap(overviewOrExplanations, logs) {
  const map = {};
  const explanations = Array.isArray(overviewOrExplanations)
    ? overviewOrExplanations
    : overviewOrExplanations?.explanations || [];
  const logList = logs || overviewOrExplanations?.logs || [];
  const logsByDay = new Map();
  logList.forEach((log) => {
    const k = workDateKey(log.work_date);
    if (!k) return;
    if (!logsByDay.has(k)) logsByDay.set(k, []);
    logsByDay.get(k).push(log);
  });
  explanations.forEach((ex) => {
    const k = workDateKey(ex.work_date);
    if (!k) return;
    map[k] = mapApiExplanationToDraft(ex, logsByDay.get(k) || []);
  });
  return map;
}

const PAYROLL_ACK_DEFAULT_LABEL = "Đồng ý với bảng công hiện tại";

/** Đồng bộ `DashboardHRM_01` và BE `dashboardScopeHelper` / `isEmployeeSuperAdmin` */
const SESSION_SUPER_ROLES = ["super admin", "superadmin", "administrator", "system administrator"];

function normalizeSessionRoleLabel(r) {
  return String(r || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

const monthDefault = () => {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** Giá trị `datetime-local` → ISO UTC gửi API */
function localDatetimeInputToIsoUtc(value) {
  if (!value || typeof value !== "string") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

function isEmployeeDeadlinePassedFromPeriod(period) {
  const raw = period?.employee_response_deadline_at;
  if (raw == null || raw === "") return false;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) && Date.now() > t;
}

function buildExplanationPlaceholder(canExplain, payrollAgreement, period, canConfirmWrite, hrReview) {
  if (!canExplain) {
    if (payrollAgreement) return "Đã xác nhận đồng ý bảng công — không chỉnh giải trình.";
    if (period?.confirmation_requested_at && isEmployeeDeadlinePassedFromPeriod(period)) {
      return "Đã hết hạn gửi giải trình / xác nhận do HCNS đặt.";
    }
    return "Chưa thể giải trình (chưa có yêu cầu hoặc kỳ đã đóng).";
  }
  if (!canConfirmWrite) return "Bạn không có quyền gửi giải trình (cần quyền Cập nhật — U).";
  if (hrReview?.status === "rejected") {
    return "HCNS không duyệt — chỉnh giải trình (nếu cần) rồi gửi lại cho HCNS...";
  }
  return "Mô tả sai sót hoặc đề xuất điều chỉnh...";
}

function hrExplanationBranchDeptLine(row) {
  const b = (row.branch_name || "").trim() || "—";
  const d = (row.department_name || "").trim() || "—";
  return `${b} • ${d}`;
}

function employeeDeptCodeLine(emp) {
  const dept = (emp?.department_name || "").trim() || "—";
  const code = (emp?.employee_code || emp?.code || "").trim();
  return code ? `${dept}, ${code}` : dept;
}

function workDurationToMinutes(wd) {
  if (!wd) return 0;
  if (typeof wd === "object" && (wd.hours != null || wd.minutes != null)) {
    return (Number(wd.hours) || 0) * 60 + (Number(wd.minutes) || 0);
  }
  if (typeof wd === "number" && Number.isFinite(wd)) return wd;
  if (typeof wd === "string" && wd.includes(":")) {
    const p = wd.split(":");
    return (parseInt(p[0], 10) || 0) * 60 + (parseInt(p[1], 10) || 0);
  }
  return 0;
}

function formatHoursFromMinutes(totalMin) {
  if (!totalMin || totalMin <= 0) return "0";
  const hours = totalMin / 60;
  return Number.isInteger(hours)
    ? String(hours)
    : hours.toFixed(1).replace(/\.0$/, "");
}

/** Tổng hợp nhanh phiếu tháng (ngày công / giờ công / OT / phép / giải trình). */
function summarizeEmployeeMonthOverview(overview, monthValue) {
  const rows = buildMonthSheetRows(monthValue, overview);
  let workDays = 0;
  let leaveDays = 0;
  let workMinutes = 0;
  rows.forEach((row) => {
    if (row.logs?.length) {
      workDays += 1;
      row.logs.forEach((log) => {
        workMinutes += workDurationToMinutes(log.work_duration);
      });
    }
    if (row.leaves?.length) leaveDays += 1;
  });

  let otMinutes = 0;
  (overview?.overtimes || []).forEach((ot) => {
    const mins = Number(ot.ot_minutes);
    if (Number.isFinite(mins) && mins > 0) otMinutes += mins;
  });

  return {
    workDays,
    leaveDays,
    workMinutes,
    workHoursLabel: formatHoursFromMinutes(workMinutes),
    otMinutes,
    otLabel: formatHoursFromMinutes(otMinutes),
    explanationCount: (overview?.explanations || []).length,
  };
}

const HrEmployeeMenuOption = (props) => {
  const { data, isSelected } = props;
  return (
    <components.Option {...props}>
      <div className="py-0.5 text-left">
        <div
          className={`text-sm font-black uppercase tracking-tight leading-snug ${
            isSelected ? "text-white" : "text-gray-800"
          }`}
        >
          {data.label}
        </div>
        {data.sub ? (
          <div
            className={`mt-1 text-[10px] font-bold leading-snug normal-case tracking-normal ${
              isSelected ? "text-red-100" : "text-gray-400"
            }`}
          >
            {data.sub}
          </div>
        ) : null}
      </div>
    </components.Option>
  );
};

const HrEmployeeSingleValue = (props) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="flex min-w-0 flex-col py-0.5 text-left">
        <span className="truncate text-sm font-black uppercase tracking-tight text-gray-800">
          {data.label}
        </span>
        {data.sub ? (
          <span className="truncate text-[10px] font-bold normal-case tracking-normal text-gray-400">
            {data.sub}
          </span>
        ) : null}
      </div>
    </components.SingleValue>
  );
};

const hrEmployeeSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...(typeof customSelectStyles.control === "function"
      ? customSelectStyles.control(provided, state)
      : provided),
    minHeight: 50,
    height: "auto",
    borderRadius: "0.75rem",
    borderColor: state.isFocused ? "#fecaca" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(254, 202, 202, 0.45)" : "none",
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingTop: 6,
    paddingBottom: 6,
  }),
  singleValue: (provided, state) => ({
    ...(typeof customSelectStyles.singleValue === "function"
      ? customSelectStyles.singleValue(provided, state)
      : provided),
    position: "relative",
    transform: "none",
    top: "auto",
    maxWidth: "100%",
    margin: 0,
  }),
  option: (provided, state) => ({
    ...(typeof customSelectStyles.option === "function"
      ? customSelectStyles.option(provided, state)
      : provided),
    paddingTop: 10,
    paddingBottom: 10,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
};

const AttendanceConfirmationTab = () => {
  const [monthValue, setMonthValue] = useState(monthDefault());
  /** Raw rows từ API — đồng nhất `/hrm/dashboard` (react-select chi nhánh) */
  const [branchRecords, setBranchRecords] = useState([]);
  const [requestBranchId, setRequestBranchId] = useState("all");
  /** Chuỗi `datetime-local` — bắt buộc khi HCNS «Gửi yêu cầu». */
  const [employeeResponseDeadlineAt, setEmployeeResponseDeadlineAt] = useState("");
  const [listBranchId, setListBranchId] = useState("all");
  const [period, setPeriod] = useState(null);
  const [overview, setOverview] = useState(null);
  const [hrExplanations, setHrExplanations] = useState([]);
  const [hrPayrollAgreements, setHrPayrollAgreements] = useState([]);
  const [hrEmployees, setHrEmployees] = useState([]);
  const [selectedHrEmployeeId, setSelectedHrEmployeeId] = useState("");
  const [hrEmployeeOverview, setHrEmployeeOverview] = useState(null);
  const [loadingHrEmployee, setLoadingHrEmployee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopeningPeriod, setReopeningPeriod] = useState(false);
  const [draftReasons, setDraftReasons] = useState({});
  const [savingDay, setSavingDay] = useState(null);
  const [deleteKey, setDeleteKey] = useState(null);
  const [exportingPayroll, setExportingPayroll] = useState(false);
  const [exportingClosedReport, setExportingClosedReport] = useState(false);
  const [acknowledgingPayroll, setAcknowledgingPayroll] = useState(false);
  const [revokingAck, setRevokingAck] = useState(false);
  /** Sub-tab trong màn xác nhận: phiếu của tôi / HCNS xem NV / xác nhận từ NV */
  const [confirmSubTab, setConfirmSubTab] = useState("my-sheet");
  /** Popup giải trình theo ngày khi click dòng đồng ý tổng (HCNS) */
  const [hrAckDetail, setHrAckDetail] = useState(null);
  const [hrAckDetailOverview, setHrAckDetailOverview] = useState(null);
  const [loadingHrAckDetail, setLoadingHrAckDetail] = useState(false);
  const [reviewingEmployee, setReviewingEmployee] = useState(false);
  const [reviewingExplanationDay, setReviewingExplanationDay] = useState(null);

  /** `hrm.attendance.confirm`: R xem tab & danh sách; C khối HCNS gửi yêu cầu/đóng kỳ; U lưu giải trình; D xóa giải trình */
  const canConfirm = hasPermission("hrm.attendance.confirm", "R");
  const canConfirmCreate = hasPermission("hrm.attendance.confirm", "C");
  const canConfirmWrite = hasPermission("hrm.attendance.confirm", "U");
  const canConfirmDelete = hasPermission("hrm.attendance.confirm", "D");
  const canHrRequest = canConfirmCreate;
  /** Danh sách giải trình đa chi nhánh + lọc: chỉ HCNS (có C), không hiển thị cho nhân viên chỉ có R/U/D */
  const canHrExplanationsList = canConfirm && canConfirmCreate;

  const confirmSubTabs = useMemo(() => {
    const tabs = [
      {
        id: "my-sheet",
        label: "Phiếu chấm công của tôi trong tháng",
        shortLabel: "Phiếu của tôi",
      },
    ];
    if (canHrExplanationsList) {
      tabs.push(
        {
          id: "hr-review",
          label: "HCNS — Xem phiếu theo nhân viên / Phê duyệt kỳ công",
          shortLabel: "Xem phiếu NV",
        },
        {
          id: "hr-confirmations",
          label: "Xác nhận từ nhân viên (HCNS)",
          shortLabel: "Xác nhận NV",
        }
      );
    }
    return tabs;
  }, [canHrExplanationsList]);

  useEffect(() => {
    if (!confirmSubTabs.some((t) => t.id === confirmSubTab)) {
      setConfirmSubTab("my-sheet");
    }
  }, [confirmSubTabs, confirmSubTab]);

  useEffect(() => {
    if (confirmSubTab !== "hr-confirmations") {
      setHrAckDetail(null);
      setHrAckDetailOverview(null);
    }
  }, [confirmSubTab]);

  useEffect(() => {
    let cancelled = false;
    const employeeId = hrAckDetail?.employee_id;
    if (!employeeId) {
      setHrAckDetailOverview(null);
      setLoadingHrAckDetail(false);
      return undefined;
    }
    setLoadingHrAckDetail(true);
    getAttendanceConfirmationEmployeeOverview({
      year_month: monthValue,
      employee_id: employeeId,
    })
      .then((data) => {
        if (!cancelled) setHrAckDetailOverview(data);
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          setHrAckDetailOverview(null);
          showErrorToast(
            e?.response?.data?.message || e?.message || "Không tải được phiếu công nhân viên."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingHrAckDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hrAckDetail?.employee_id, monthValue]);

  const isSessionSuperAdmin = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      const roles = Array.isArray(u.roles) ? u.roles : [];
      return roles.some((r) => SESSION_SUPER_ROLES.includes(normalizeSessionRoleLabel(r)));
    } catch {
      return false;
    }
  }, []);

  const loadPeriod = useCallback(async () => {
    const data = await getAttendanceConfirmationPeriod(monthValue);
    setPeriod(data.period || null);
  }, [monthValue]);

  const loadOverview = useCallback(async () => {
    const data = await getMyAttendanceConfirmationOverview(monthValue);
    setOverview(data);
    setDraftReasons(buildExplanationDraftMap(data));
  }, [monthValue]);

  const loadHrExplanations = useCallback(async () => {
    if (!canHrExplanationsList) return;
    const branch_id = listBranchId && listBranchId !== "all" ? listBranchId : undefined;
    const data = await getAttendanceConfirmationExplanations({
      year_month: monthValue,
      branch_id,
    });
    setHrExplanations(data.items || []);
  }, [monthValue, listBranchId, canHrExplanationsList]);

  const loadHrPayrollAgreements = useCallback(async () => {
    if (!canHrExplanationsList) return;
    const branch_id = listBranchId && listBranchId !== "all" ? listBranchId : undefined;
    const data = await getAttendancePayrollAgreements({
      year_month: monthValue,
      branch_id,
    });
    setHrPayrollAgreements(data.items || []);
  }, [monthValue, listBranchId, canHrExplanationsList]);

  const loadHrEmployees = useCallback(async () => {
    if (!canHrExplanationsList) return;
    const branch_id = listBranchId && listBranchId !== "all" ? listBranchId : undefined;
    const data = await getAttendanceConfirmationEmployees({
      year_month: monthValue,
      branch_id,
    });
    const items = data.items || [];
    setHrEmployees(items);
    setSelectedHrEmployeeId((prev) => {
      if (prev && items.some((e) => String(e.employee_id) === String(prev))) return prev;
      return items[0]?.employee_id ? String(items[0].employee_id) : "";
    });
  }, [monthValue, listBranchId, canHrExplanationsList]);

  const loadHrEmployeeOverview = useCallback(
    async (employeeId) => {
      if (!canHrExplanationsList || !employeeId) {
        setHrEmployeeOverview(null);
        return;
      }
      setLoadingHrEmployee(true);
      try {
        const data = await getAttendanceConfirmationEmployeeOverview({
          year_month: monthValue,
          employee_id: employeeId,
        });
        setHrEmployeeOverview(data);
      } catch (e) {
        console.error(e);
        setHrEmployeeOverview(null);
        showErrorToast(
          e?.response?.data?.message || e?.message || "Không tải được phiếu công nhân viên."
        );
      } finally {
        setLoadingHrEmployee(false);
      }
    },
    [monthValue, canHrExplanationsList]
  );

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadPeriod(),
        loadOverview(),
        loadHrExplanations(),
        loadHrPayrollAgreements(),
        loadHrEmployees(),
      ]);
    } catch (e) {
      console.error(e);
      showErrorToast(e?.message || "Không tải được dữ liệu xác nhận giờ công.");
    } finally {
      setLoading(false);
    }
  }, [loadPeriod, loadOverview, loadHrExplanations, loadHrPayrollAgreements, loadHrEmployees]);

  const branchSelectOptions = useMemo(
    () => buildHrmDashboardBranchSelectOptions(branchRecords),
    [branchRecords]
  );

  useEffect(() => {
    getAllBranches_Array()
      .then((list) => setBranchRecords(Array.isArray(list) ? list : []))
      .catch(console.error);
  }, []);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    loadHrEmployeeOverview(selectedHrEmployeeId);
  }, [selectedHrEmployeeId, loadHrEmployeeOverview]);

  const monthSheetRows = useMemo(
    () => buildMonthSheetRows(monthValue, overview),
    [overview, monthValue]
  );

  const hrEmployeeSelectOptions = useMemo(
    () =>
      hrEmployees.map((emp) => ({
        value: String(emp.employee_id),
        label: (emp.employee_name || "—").trim() || "—",
        sub: employeeDeptCodeLine(emp),
        meta: emp,
      })),
    [hrEmployees]
  );

  const selectedHrEmployeeOption =
    hrEmployeeSelectOptions.find((o) => o.value === String(selectedHrEmployeeId)) || null;

  const hrEmployeeDraftReasons = useMemo(
    () => buildExplanationDraftMap(hrEmployeeOverview),
    [hrEmployeeOverview]
  );

  const selectedHrEmployeeMeta = useMemo(
    () => hrEmployees.find((e) => String(e.employee_id) === String(selectedHrEmployeeId)) || null,
    [hrEmployees, selectedHrEmployeeId]
  );

  const hrEmployeeMonthStats = useMemo(
    () => summarizeEmployeeMonthOverview(hrEmployeeOverview, monthValue),
    [hrEmployeeOverview, monthValue]
  );

  const hrAckDetailExplanations = useMemo(() => {
    if (!hrAckDetail?.employee_id) return [];
    return (hrExplanations || []).filter(
      (ex) => String(ex.employee_id) === String(hrAckDetail.employee_id)
    );
  }, [hrExplanations, hrAckDetail]);

  const hrAckDetailDraftReasons = useMemo(() => {
    if (hrAckDetailOverview?.explanations?.length) {
      return buildExplanationDraftMap(hrAckDetailOverview);
    }
    return buildExplanationDraftMap(hrAckDetailExplanations, hrAckDetailOverview?.logs || []);
  }, [hrAckDetailOverview, hrAckDetailExplanations]);

  const hrAckDetailSavedSet = useMemo(() => {
    const set = new Set();
    Object.keys(hrAckDetailDraftReasons).forEach((k) => {
      if (String(hrAckDetailDraftReasons[k]?.reason || "").trim()) set.add(k);
    });
    return set;
  }, [hrAckDetailDraftReasons]);

  const explanationCountByEmployee = useMemo(() => {
    const map = new Map();
    (hrExplanations || []).forEach((ex) => {
      const id = String(ex.employee_id || "");
      if (!id) return;
      map.set(id, (map.get(id) || 0) + 1);
    });
    return map;
  }, [hrExplanations]);

  /** Kỳ đang mở và đã có yêu cầu xác nhận — NV trong cửa sổ; HCNS không gửi yêu cầu trùng từ UI. */
  const hrNvConfirmWindowActive = Boolean(
    period?.status === "open" && period?.confirmation_requested_at
  );
  const disableHrRequestButton =
    requesting || period?.status === "closed" || hrNvConfirmWindowActive;
  const disableHrCloseButton =
    closing || !period || period?.status === "closed" || !hrNvConfirmWindowActive;

  const periodLabel = useMemo(() => {
    if (!period) return "Chưa có kỳ bảng công cho tháng này.";
    if (period.status === "closed") return "Kỳ đã đóng — không thể giải trình.";
    if (!period.confirmation_requested_at) return "HCNS chưa gửi yêu cầu xác nhận.";
    const dl = period.employee_response_deadline_at;
    const passed = isEmployeeDeadlinePassedFromPeriod(period);
    if (passed && period.status === "open") {
      return `Đã hết hạn nhập liệu nhân viên (${formatDateTime(dl)}). App không nhận thêm giải trình hay xác nhận đồng ý — HCNS có thể vẫn chưa «đóng kỳ».`;
    }
    if (period.status === "open" && dl) {
      return `Đang trong thời hạn nhân viên gửi giải trình và/hoặc xác nhận đồng ý tổng. Hết hạn: ${formatDateTime(dl)}.`;
    }
    return "Đang mở — nhân viên có thể đồng ý với bảng công hiện tại hoặc giải trình từng ngày cần chỉnh (kỳ cũ chưa gắn hạn HCNS có thể chưa thiết lập).";
  }, [period]);

  const handleRequest = async () => {
    const deadlineIso = localDatetimeInputToIsoUtc(employeeResponseDeadlineAt);
    if (!deadlineIso) {
      showErrorToast("Chọn đủ ngày và giờ «hết hạn nhận từ nhân viên» (bắt buộc).");
      return;
    }
    const ok = await showConfirmDialog({
      title: "Gửi yêu cầu xác nhận giờ công?",
      text: `Mốc hết hạn nhập liệu từ nhân viên (ghi nhận theo máy chủ): ${employeeResponseDeadlineAt.replace("T", " ")}. Áp dụng tháng ${monthValue}.`,
      confirmText: "Gửi",
      cancelText: "Huỷ",
    });
    if (!ok.isConfirmed) return;
    try {
      setRequesting(true);
      const res = await postAttendanceConfirmationRequest({
        year_month: monthValue,
        branch_id: requestBranchId && requestBranchId !== "all" ? requestBranchId : undefined,
        employee_response_deadline_at: deadlineIso,
      });
      showSuccessToast(res.message || "Đã gửi yêu cầu.");
      await refreshAll();
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không gửi được yêu cầu.");
    } finally {
      setRequesting(false);
    }
  };

  const handleExportPayrollXlsx = async () => {
    const parsedYm = parseMonthKey(monthValue);
    if (!parsedYm) {
      showErrorToast("Tháng áp dụng không hợp lệ.");
      return;
    }
    const branchLabel =
      branchSelectOptions.find((o) => String(o.value) === String(requestBranchId))?.label || "Tất cả chi nhánh";
    try {
      setExportingPayroll(true);
      const data = await getAttendanceConfirmationPayrollExportData({
        year_month: monthValue,
        branch_id: requestBranchId && requestBranchId !== "all" ? requestBranchId : undefined,
      });
      const records = data?.records || [];
      if (!records.length) {
        showErrorToast("Không có dữ liệu chấm công để xuất trong tháng và phạm vi đã chọn.");
        return;
      }

      const pivotAoA = buildPivotAoA(records, parsedYm.year, parsedYm.month, "actual_hours");

      await downloadPayrollConfirmationExcel({
        pivotAoA,
        year: parsedYm.year,
        month: parsedYm.month,
        branchLabel,
        filename: `bang-cong-ke-toan_${monthValue}.xlsx`,
        period: data.period || null,
        explanations: data.explanations || [],
      });

      showSuccessToast("Đã tạo file Excel (2 sheet: giờ thực tế theo ca + giải trình).");
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không xuất được file Excel.");
    } finally {
      setExportingPayroll(false);
    }
  };

  const handleDownloadClosedPeriodReportPdf = async () => {
    if (period?.status !== "closed") {
      showErrorToast("Chỉ tải báo cáo PDF sau khi đã đóng kỳ công.");
      return;
    }
    try {
      setExportingClosedReport(true);
      const data = await getAttendanceConfirmationClosedPeriodReport({
        year_month: monthValue,
        branch_id: requestBranchId && requestBranchId !== "all" ? requestBranchId : undefined,
      });
      await downloadClosedPeriodReportPdf(data, {
        filename: `bao-cao-ky-cong_${monthValue}.pdf`,
      });
      showSuccessToast("Đã tải báo cáo PDF kỳ công.");
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không tải được báo cáo PDF.");
    } finally {
      setExportingClosedReport(false);
    }
  };

  const handleClosePeriod = async () => {
    const ok = await showConfirmDialog({
      title: "Phê duyệt / đóng kỳ công tháng này?",
      text: "Sau khi phê duyệt và đóng kỳ, nhân viên không thể gửi hoặc sửa giải trình. Kỳ bảng công được chốt để bàn giao kế toán.",
      confirmText: "Phê duyệt & đóng kỳ",
      cancelText: "Huỷ",
    });
    if (!ok.isConfirmed) return;
    try {
      setClosing(true);
      const res = await postAttendanceConfirmationClose({ year_month: monthValue });
      showSuccessToast(res.message || "Đã phê duyệt và đóng kỳ công.");
      await refreshAll();
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không đóng được kỳ.");
    } finally {
      setClosing(false);
    }
  };

  const handleEmployeeReview = async (status) => {
    if (!selectedHrEmployeeId) {
      showErrorToast("Chọn nhân viên trước khi duyệt.");
      return;
    }
    const empName =
      hrEmployeeOverview?.employee?.employee_name ||
      selectedHrEmployeeMeta?.employee_name ||
      "nhân viên";
    const isApprove = status === "approved";

    let note = null;
    if (!isApprove) {
      note = await showInputDialog({
        title: `Không duyệt phiếu công — ${empName}`,
        inputLabel: "Lý do không duyệt (bắt buộc)",
        inputType: "textarea",
        inputPlaceholder: "Nhập lý do để nhân viên chỉnh sửa và gửi lại...",
        confirmText: "Không duyệt",
        inputValidator: (value) => {
          if (!value || !String(value).trim()) return "Vui lòng nhập lý do không duyệt.";
          return null;
        },
      });
      if (note == null) return;
      note = String(note).trim();
    } else {
      const ok = await showConfirmDialog({
        title: "Duyệt phiếu công nhân viên?",
        text: `Xác nhận duyệt phiếu công tháng ${monthValue} của ${empName}.`,
        confirmText: "Duyệt",
        cancelText: "Huỷ",
      });
      if (!ok.isConfirmed) return;
    }

    try {
      setReviewingEmployee(true);
      const res = await postAttendanceConfirmationEmployeeReview({
        year_month: monthValue,
        employee_id: selectedHrEmployeeId,
        status,
        ...(note ? { note } : {}),
      });
      showSuccessToast(res.message || (isApprove ? "Đã duyệt." : "Đã đánh dấu không duyệt."));
      await Promise.all([loadHrEmployees(), loadHrEmployeeOverview(selectedHrEmployeeId)]);
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không lưu được kết quả duyệt.");
    } finally {
      setReviewingEmployee(false);
    }
  };

  const handleReopenPeriod = async () => {
    const ok = await showConfirmDialog({
      title: "Mở lại kỳ bảng công?",
      text: "Chỉ nên dùng khi có sai sót sau khi đóng kỳ. Nhân viên có thể gửi hoặc chỉnh giải trình lại sau khi HCNS đã yêu cầu xác nhận và kỳ không còn bị đóng.",
      confirmText: "Mở lại kỳ",
      cancelText: "Huỷ",
    });
    if (!ok.isConfirmed) return;
    try {
      setReopeningPeriod(true);
      const res = await postAttendanceConfirmationReopenPeriod({ year_month: monthValue });
      showSuccessToast(res.message || "Đã mở lại kỳ.");
      await refreshAll();
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không mở lại được kỳ.");
    } finally {
      setReopeningPeriod(false);
    }
  };

  const handleSaveExplanation = async (workDate) => {
    const draft = draftReasons[workDate] || emptyDraftExplanation([]);
    const reason = String(draft.reason || "").trim();
    if (!reason) {
      showErrorToast("Nhập lý do giải trình.");
      return;
    }
    const explanation_type = draft.explanation_type || "other";
    if (explanation_type === "missing_checkout" && !draft.proposed_check_out) {
      showErrorToast("Nhập giờ ra đề xuất.");
      return;
    }
    if (explanation_type === "missing_day" && (!draft.proposed_check_in || !draft.proposed_check_out)) {
      showErrorToast("Nhập đủ giờ vào và giờ ra đề xuất.");
      return;
    }
    try {
      setSavingDay(workDate);
      await postAttendanceHourExplanation({
        year_month: monthValue,
        work_date: workDate,
        reason,
        explanation_type,
        proposed_check_in: draft.proposed_check_in || null,
        proposed_check_out: draft.proposed_check_out || null,
      });
      showSuccessToast("Đã lưu giải trình.");
      await loadOverview();
      await loadHrExplanations();
      await loadHrPayrollAgreements();
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không lưu được.");
    } finally {
      setSavingDay(null);
    }
  };

  const handleDeleteExplanation = async (workDate) => {
    const ok = await showConfirmDialog({
      title: "Xóa giải trình?",
      text: "Nội dung giải trình đã lưu cho ngày này sẽ bị gỡ khỏi hệ thống.",
      confirmText: "Xóa",
      cancelText: "Huỷ",
    });
    if (!ok.isConfirmed) return;
    try {
      setDeleteKey(`own:${workDate}`);
      await deleteAttendanceHourExplanation({
        year_month: monthValue,
        work_date: workDate,
      });
      showSuccessToast("Đã xóa giải trình.");
      setDraftReasons((prev) => {
        const next = { ...prev };
        delete next[workDate];
        return next;
      });
      await loadOverview();
      await loadHrExplanations();
      await loadHrPayrollAgreements();
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không xóa được.");
    } finally {
      setDeleteKey(null);
    }
  };

  const handleHrExplanationReview = async (employeeId, workDate, status, reloadFns = []) => {
    if (!employeeId || !workDate) return;
    let review_note = null;
    if (status === "rejected") {
      review_note = await showInputDialog({
        title: "Không duyệt giải trình",
        inputLabel: "Lý do không duyệt (bắt buộc)",
        inputType: "textarea",
        inputPlaceholder: "Nhập lý do để nhân viên chỉnh lại...",
        confirmText: "Không duyệt",
        inputValidator: (value) => {
          if (!value || !String(value).trim()) return "Vui lòng nhập lý do.";
          return null;
        },
      });
      if (review_note == null) return;
      review_note = String(review_note).trim();
    } else {
      const ok = await showConfirmDialog({
        title: "Duyệt giải trình?",
        text:
          "Nếu giải trình có đề xuất giờ (thiếu checkout / quên chấm), hệ thống sẽ cập nhật hoặc tạo phiếu chấm công tương ứng.",
        confirmText: "Duyệt & áp số liệu",
        cancelText: "Huỷ",
      });
      if (!ok.isConfirmed) return;
    }
    try {
      setReviewingExplanationDay(workDate);
      const res = await postAttendanceExplanationReview({
        year_month: monthValue,
        employee_id: employeeId,
        work_date: workDate,
        status,
        ...(review_note ? { review_note } : {}),
      });
      showSuccessToast(res.message || "Đã cập nhật giải trình.");
      await loadHrExplanations();
      await loadHrEmployees();
      for (const fn of reloadFns) {
        // eslint-disable-next-line no-await-in-loop
        await fn?.();
      }
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không duyệt được giải trình.");
    } finally {
      setReviewingExplanationDay(null);
    }
  };

  const handleDeleteHrExplanation = async (row) => {
    const wd = workDateKey(row.work_date);
    if (!wd) return false;
    const ok = await showConfirmDialog({
      title: "Xóa giải trình nhân viên?",
      text: `${row.employee_name} — ${formatYmdKeyVN(wd)}`,
      confirmText: "Xóa",
      cancelText: "Huỷ",
    });
    if (!ok.isConfirmed) return false;
    try {
      setDeleteKey(`hr:${row.employee_id}:${wd}`);
      await deleteAttendanceHourExplanation({
        year_month: monthValue,
        work_date: wd,
        employee_id: row.employee_id,
      });
      showSuccessToast("Đã xóa giải trình.");
      await loadHrExplanations();
      await loadHrPayrollAgreements();
      await loadOverview();
      return true;
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không xóa được.");
      return false;
    } finally {
      setDeleteKey(null);
    }
  };

  const closeHrAckDetail = () => {
    setHrAckDetail(null);
    setHrAckDetailOverview(null);
  };

  const handleDeleteHrExplanationByDay = async (dayKey) => {
    if (!hrAckDetail?.employee_id || !dayKey) return;
    const source = hrAckDetailOverview?.explanations?.length
      ? hrAckDetailOverview.explanations
      : hrAckDetailExplanations;
    const row =
      (source || []).find(
        (ex) =>
          String(ex.employee_id) === String(hrAckDetail.employee_id) &&
          workDateKey(ex.work_date) === dayKey
      ) || {
        employee_id: hrAckDetail.employee_id,
        work_date: dayKey,
        employee_name: hrAckDetail.employee_name,
      };
    const deleted = await handleDeleteHrExplanation(row);
    if (!deleted) return;
    try {
      const data = await getAttendanceConfirmationEmployeeOverview({
        year_month: monthValue,
        employee_id: hrAckDetail.employee_id,
      });
      setHrAckDetailOverview(data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAcknowledgePayroll = async () => {
    const isResubmit = overview?.hr_review?.status === "rejected";
    const ok = await showConfirmDialog({
      title: isResubmit ? "Gửi lại bảng công cho HCNS?" : "Xác nhận đồng ý bảng công?",
      text: isResubmit
        ? `Gửi lại phiếu công tháng ${monthValue} (kèm giải trình đã chỉnh, nếu có) để HCNS xét duyệt lại. Sau khi gửi bạn không chỉnh giải trình trên app nữa cho đến khi HCNS phản hồi.`
        : `Hệ thống ghi nhận "${PAYROLL_ACK_DEFAULT_LABEL}" cho tháng ${monthValue} — bao gồm các ngày bạn đã gửi giải trình (nếu có). Sau khi gửi, bạn không chỉnh/xóa giải trình trên app nữa để HCNS tổng kết ổn định. Chỉ huỷ xác nhận tổng được trong thời hạn HCNS mở (trước thời điểm hết hạn đã đặt).`,
      confirmText: isResubmit ? "Gửi lại" : "Gửi xác nhận",
      cancelText: "Huỷ",
    });
    if (!ok.isConfirmed) return;
    try {
      setAcknowledgingPayroll(true);
      await postAcknowledgeAttendancePayroll({ year_month: monthValue });
      showSuccessToast(
        isResubmit ? "Đã gửi lại bảng công cho HCNS." : "Đã ghi nhận xác nhận đồng ý bảng công."
      );
      await loadOverview();
      await loadHrPayrollAgreements();
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không gửi được xác nhận.");
    } finally {
      setAcknowledgingPayroll(false);
    }
  };

  const handleRevokeAcknowledgePayroll = async () => {
    const ok = await showConfirmDialog({
      title: "Huỷ xác nhận đồng ý?",
      text: "Sau khi huỷ trong thời hạn còn mở, bạn chỉnh lại giải trình từng ngày nếu cần rồi có thể gửi xác nhận tổng lại. Sau thời hạn HCNS đặt không còn huỷ được trên app.",
      confirmText: "Huỷ xác nhận",
      cancelText: "Đóng",
    });
    if (!ok.isConfirmed) return;
    try {
      setRevokingAck(true);
      await deleteAttendancePayrollAcknowledgment({ year_month: monthValue });
      showSuccessToast("Đã huỷ xác nhận.");
      await loadOverview();
      await loadHrPayrollAgreements();
    } catch (e) {
      console.error(e);
      showErrorToast(e?.response?.data?.message || e?.message || "Không huỷ được.");
    } finally {
      setRevokingAck(false);
    }
  };

  const payrollAgreement = overview?.payroll_agreement || null;
  const myHrReview = overview?.hr_review || null;
  const isHrRejected = myHrReview?.status === "rejected";

  const canExplain = Boolean(overview?.can_explain);
  const canAcknowledgePayroll = Boolean(overview?.can_acknowledge_payroll);
  const canRevokePayrollAgreement = Boolean(overview?.can_revoke_payroll_agreement);

  const explanationPlaceholder = useMemo(
    () =>
      buildExplanationPlaceholder(
        canExplain,
        payrollAgreement,
        period,
        canConfirmWrite,
        myHrReview
      ),
    [canExplain, payrollAgreement, period, canConfirmWrite, myHrReview]
  );

  const hasSavedExplanationOnServer = useMemo(() => {
    const set = new Set();
    (overview?.explanations || []).forEach((ex) => {
      const k = workDateKey(ex.work_date);
      if (k) set.add(k);
    });
    return set;
  }, [overview]);

  if (!canConfirm) {
    return (
      <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-6 text-center text-sm font-semibold text-amber-900">
        Bạn không có quyền xem tab Xác nhận / Giải trình giờ công (cần quyền Đọc — R — trên mã hrm.attendance.confirm). Liên hệ HCNS để được cấp quyền.
      </div>
    );
  }

  return (
    <div className="mx-auto min-w-0 max-w-full space-y-4 sm:space-y-6">
      <div
        className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:p-4 md:p-5"
        data-tour="hrm-attendance-confirm-intro"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 items-start gap-2 sm:gap-3">
            <div className="shrink-0 rounded-2xl bg-red-50 p-2.5 text-red-700 sm:p-3">
              <ClipboardCheck className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-black uppercase tracking-tight text-gray-800 sm:text-lg">
                Xác nhận / Giải trình giờ công
              </h2>
              <p className="mt-1 text-xs leading-relaxed text-gray-500 sm:text-sm">
                Quy trình: HCNS xuất bảng công → gửi yêu cầu xác nhận → nhân viên kiểm tra và giải trình (nếu sai)
                → HCNS đóng kỳ → Kế toán chốt lương.
              </p>
            </div>
          </div>

          <div className="flex w-full min-w-0 flex-shrink-0 flex-row items-end gap-2 lg:w-auto lg:max-w-none">
            <div className="min-w-0 flex-1">
              <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                Tháng áp dụng
              </label>
              <input
                type="month"
                value={monthValue}
                onChange={(e) => setMonthValue(e.target.value)}
                className="h-11 min-h-[2.75rem] w-full min-w-0 max-w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-700 outline-none focus:border-red-400 focus:bg-white focus:ring-2 focus:ring-red-100 sm:max-w-[11.25rem]"
              />
            </div>
            <button
              type="button"
              onClick={() => refreshAll()}
              disabled={loading}
              title="Làm mới dữ liệu"
              aria-label="Làm mới dữ liệu"
              className="inline-flex h-11 min-h-[2.75rem] min-w-[2.75rem] shrink-0 items-center justify-center self-end rounded-xl border border-gray-200 bg-white text-gray-700 hover:border-red-200 hover:bg-red-50 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2.2} />
              ) : (
                <RefreshCw className="h-5 w-5" strokeWidth={2.2} />
              )}
            </button>
          </div>
        </div>

        <div
          className={`mt-3 rounded-xl border px-3 py-3 text-xs font-semibold leading-snug sm:mt-4 sm:px-4 sm:text-sm ${
            period?.status === "closed"
              ? "border-amber-200 bg-amber-50 text-amber-900"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
        >
          {periodLabel}
        </div>

        {period?.status === "closed" && isSessionSuperAdmin && (
          <div className="mt-3 rounded-xl border border-violet-200 bg-violet-50/90 px-3 py-3 sm:mt-4 sm:px-4">
            <p className="text-xs font-semibold leading-snug text-violet-950 sm:text-sm">
              Super Admin: mở lại kỳ khi cần chỉnh sau đóng — HCNS không có quyền tự mở kỳ đã đóng.
            </p>
            <button
              type="button"
              onClick={handleReopenPeriod}
              disabled={reopeningPeriod}
              className="mt-2 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-violet-300 bg-white px-4 text-sm font-bold text-violet-900 shadow-sm hover:bg-violet-100 disabled:opacity-50 sm:w-auto"
            >
              {reopeningPeriod ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" strokeWidth={2.2} />
              ) : (
                <Unlock className="h-4 w-4 shrink-0" strokeWidth={2.2} />
              )}
              Mở lại kỳ bảng công
            </button>
          </div>
        )}

        {canHrRequest && (
          <div className="mt-4 rounded-xl border border-red-100 bg-red-50/40 p-3 sm:mt-6 sm:rounded-2xl sm:p-4">
            <p className="mb-2 text-xs font-black uppercase tracking-wide text-red-800 sm:mb-3">
              HCNS — Gửi yêu cầu / Đóng kỳ
            </p>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between xl:gap-4">
                <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-end sm:gap-3">
                  <div className="min-w-0 flex-1 space-y-1 sm:max-w-[20rem] lg:max-w-[18rem]">
                    <label className="block text-[11px] font-bold text-gray-600">
                      Chi nhánh nhận thông báo (tuỳ chọn)
                    </label>
                    <HrmDashboardBranchPicker
                      instanceId="hrm-attendance-confirm-branch-request"
                      classNamePrefix="hrm-dash-branch-att-confirm-req"
                      className="w-full"
                      compactControlHeight
                      options={branchSelectOptions}
                      value={requestBranchId}
                      onChange={setRequestBranchId}
                      aria-label="Lọc chi nhánh khi gửi yêu cầu xác nhận giờ công"
                    />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1 sm:max-w-[22rem] lg:max-w-[20rem]">
                    <label className="block text-[11px] font-bold text-gray-600">
                      Thời điểm hết hạn NV <span className="text-red-700">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      step={60}
                      value={employeeResponseDeadlineAt}
                      onChange={(e) => setEmployeeResponseDeadlineAt(e.target.value)}
                      disabled={period?.status === "closed"}
                      className="h-11 min-h-[2.75rem] w-full rounded-xl border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 disabled:opacity-50"
                    />
                  </div>
                </div>
                <div className="flex w-full shrink-0 flex-col gap-2 sm:flex-row sm:justify-end xl:w-auto xl:justify-end xl:gap-2">
                  <button
                    type="button"
                    onClick={handleRequest}
                    disabled={disableHrRequestButton}
                    title={
                      hrNvConfirmWindowActive
                        ? "Đã gửi yêu cầu xác nhận cho tháng này — dùng «Đóng kỳ» khi xong."
                        : undefined
                    }
                    className="inline-flex h-11 min-h-[2.75rem] w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-red-700 px-4 text-sm font-bold text-white shadow-sm hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-gray-400 sm:w-auto sm:min-w-[10rem] xl:whitespace-nowrap"
                  >
                    {requesting ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Send className="h-4 w-4 shrink-0" />}
                    <span className="truncate">Yêu cầu xác nhận giờ công</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleClosePeriod}
                    disabled={disableHrCloseButton}
                    title={
                      !hrNvConfirmWindowActive && period?.status === "open"
                        ? "Chỉ đóng kỳ sau khi đã gửi yêu cầu xác nhận giờ công."
                        : undefined
                    }
                    className="inline-flex h-11 min-h-[2.75rem] w-full shrink-0 items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 text-sm font-bold text-gray-800 hover:bg-gray-50 disabled:opacity-50 sm:w-auto sm:min-w-[10rem] xl:whitespace-nowrap"
                  >
                    {closing ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Lock className="h-4 w-4 shrink-0" />}
                    Phê duyệt / Đóng kỳ công
                  </button>
                </div>
              </div>
              <p className="text-[10px] leading-snug text-gray-500">
                Đến mốc này hệ thống khóa giải trình và xác nhận đồng ý của nhân viên, kể cả khi kỳ chưa đóng.
              </p>
              <div className="rounded-xl border border-dashed border-red-200/80 bg-white/60 px-3 py-3 sm:px-4">
                <p className="mb-2 text-[11px] font-black uppercase tracking-wide text-red-900">
                  Kết xuất cho kế toán / lưu hồ sơ
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <p className="min-w-0 flex-1 text-[11px] leading-relaxed text-gray-600">
                    Excel: ma trận giờ công theo ca + giải trình. PDF: báo cáo tổng hợp sau khi{" "}
                    <span className="font-semibold text-gray-700">đóng kỳ</span> để HCNS in lưu hồ sơ (theo chi nhánh lọc
                    phía trên và phạm vi dữ liệu của bạn).
                  </p>
                  <div className="flex w-full shrink-0 flex-row flex-wrap justify-end gap-2 sm:ml-4 sm:w-auto">
                    <button
                      type="button"
                      onClick={() => handleExportPayrollXlsx()}
                      disabled={exportingPayroll}
                      className="inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-sm font-bold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50 sm:flex-none sm:px-4"
                    >
                      {exportingPayroll ? (
                        <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
                      ) : (
                        <FileSpreadsheet className="h-5 w-5 shrink-0" strokeWidth={2.2} />
                      )}
                      Xuất Excel
                    </button>
                    <button
                      type="button"
                      onClick={handleDownloadClosedPeriodReportPdf}
                      disabled={exportingClosedReport || period?.status !== "closed"}
                      title={
                        period?.status !== "closed"
                          ? "Chỉ tải báo cáo PDF sau khi đã phê duyệt / đóng kỳ công."
                          : "Tải báo cáo PDF để in lưu hồ sơ"
                      }
                      className="inline-flex h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 text-sm font-bold text-red-900 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none sm:px-4"
                    >
                      {exportingClosedReport ? (
                        <Loader2 className="h-5 w-5 shrink-0 animate-spin" />
                      ) : (
                        <FileDown className="h-5 w-5 shrink-0" strokeWidth={2.2} />
                      )}
                      Tải báo cáo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="min-w-0" data-tour="hrm-attendance-confirm-subtabs">
        {confirmSubTabs.length > 1 ? (
          <>
        <div className="md:hidden">
          <div className="rounded-2xl border border-gray-100 bg-white p-1 shadow-sm">
            <div
              role="tablist"
              aria-label="Xác nhận giờ công — chọn mục"
              className="flex gap-1 overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {confirmSubTabs.map((tab) => {
                const isActive = confirmSubTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    title={tab.label}
                    onClick={() => setConfirmSubTab(tab.id)}
                    className={`flex shrink-0 snap-start items-center rounded-xl px-3 py-2.5 text-[11px] font-black uppercase tracking-wide whitespace-nowrap transition-all ${
                      isActive
                        ? "bg-red-700 text-white shadow-sm shadow-red-200/60"
                        : "text-gray-600 hover:bg-gray-50 active:bg-gray-100"
                    }`}
                  >
                    {tab.shortLabel}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="hidden flex-wrap gap-2 border-b border-gray-200 pb-1 md:flex">
          {confirmSubTabs.map((tab) => {
            const isActive = confirmSubTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                title={tab.label}
                onClick={() => setConfirmSubTab(tab.id)}
                className={`max-w-full rounded-t-xl px-4 py-2.5 text-left text-sm font-black uppercase tracking-wide transition-colors sm:px-5 ${
                  isActive
                    ? "border border-b-0 border-gray-200 -mb-px bg-white text-red-800"
                    : "text-gray-500 hover:text-gray-800"
                }`}
              >
                <span className="line-clamp-2">{tab.label}</span>
              </button>
            );
          })}
        </div>
          </>
        ) : null}
      </div>

      {confirmSubTab === "my-sheet" ? (
      <div
        className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:p-4 md:p-5"
        data-tour="hrm-attendance-confirm-sheet"
      >
        <h3 className="mb-3 text-sm font-black uppercase tracking-wide text-gray-700">
          Phiếu chấm công của tôi trong tháng
        </h3>
        {monthSheetRows.length > 0 &&
        canConfirmWrite &&
        (payrollAgreement || canAcknowledgePayroll || canRevokePayrollAgreement || isHrRejected) ? (
          <div className="mb-4 space-y-3 rounded-xl border border-gray-100 bg-gray-50/80 p-3 sm:p-4">
            <p className="text-[11px] font-black uppercase tracking-wide text-gray-500">
              Xác nhận đồng ý tổng bảng công
            </p>
            {isHrRejected && !payrollAgreement ? (
              <div className="rounded-xl border border-red-200 bg-red-50/90 px-3 py-3 text-sm leading-relaxed">
                <div className="flex flex-wrap items-start gap-2">
                  <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-700" strokeWidth={2.2} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-red-950">HCNS không duyệt bảng công</p>
                    {myHrReview?.note ? (
                      <p className="mt-1 text-red-900">
                        <span className="font-semibold">Lý do:</span> {myHrReview.note}
                      </p>
                    ) : null}
                    {myHrReview?.reviewed_at ? (
                      <p className="mt-2 text-[11px] font-semibold text-red-800/90">
                        Thời điểm: {formatDateTime(myHrReview.reviewed_at)}
                      </p>
                    ) : null}
                    <p className="mt-2 text-xs leading-relaxed text-red-950/90">
                      Bạn có thể chỉnh giải trình theo ngày rồi bấm gửi lại cho HCNS xét duyệt.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
            {payrollAgreement ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/90 px-3 py-3 text-sm leading-relaxed">
                <div className="flex flex-wrap items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" strokeWidth={2.2} aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-emerald-950">Đã gửi xác nhận</p>
                    <p className="mt-1 text-emerald-900">{payrollAgreement.message || PAYROLL_ACK_DEFAULT_LABEL}</p>
                    <p className="mt-2 text-[11px] font-semibold text-emerald-800/90">
                      Ghi nhận lúc: {formatDateTime(payrollAgreement.acknowledged_at)}
                    </p>
                  </div>
                </div>
                {canRevokePayrollAgreement ? (
                  <button
                    type="button"
                    onClick={handleRevokeAcknowledgePayroll}
                    disabled={revokingAck}
                    className="mt-3 inline-flex h-11 w-full items-center justify-center rounded-xl border border-emerald-300 bg-white px-4 text-xs font-bold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50 sm:w-auto"
                  >
                    {revokingAck ? (
                      <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} />
                    ) : (
                      "Huỷ xác nhận tổng"
                    )}
                  </button>
                ) : (
                  <p className="mt-3 text-xs font-semibold text-emerald-900">
                    Đã khóa giải trình theo ngày để HCNS tổng kết. Huỷ xác nhận không khả dụng sau khi hết thời hạn HCNS
                    đặt hoặc khi HCNS đã duyệt.
                  </p>
                )}
                <p className="mt-2 text-xs leading-relaxed text-emerald-950/90">
                  Sau khi xác nhận tổng, bạn không chỉnh giải trình trên app. Cần chỉnh ngoại lệ do HCNS xử lý.
                </p>
              </div>
            ) : null}
            {canAcknowledgePayroll ? (
              <>
                <p className="text-xs leading-relaxed text-gray-600">
                  {isHrRejected ? (
                    <>
                      Sau khi chỉnh giải trình (nếu cần), bấm{" "}
                      <span className="font-semibold">Gửi lại cho HCNS</span> để xét duyệt lại phiếu công tháng này.
                    </>
                  ) : (
                    <>
                      Sau khi kiểm tra phiếu công và mọi{" "}
                      <span className="font-semibold">giải trình đã gửi</span> (nếu có), bấm một lần để báo HCNS là bạn{" "}
                      <span className="font-semibold">đồng ý với kết quả hiển thị trong tab</span> cho tháng này (kèm các
                      giải trình đã lưu). Sau khi gửi sẽ không chỉnh giải trình được nữa.
                    </>
                  )}
                </p>
                <button
                  type="button"
                  onClick={handleAcknowledgePayroll}
                  disabled={acknowledgingPayroll}
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 text-sm font-bold text-white shadow-sm hover:bg-emerald-800 disabled:opacity-50 sm:w-auto"
                >
                  {acknowledgingPayroll ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin" strokeWidth={2.2} />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 shrink-0" strokeWidth={2.2} />
                  )}
                  {isHrRejected ? "Gửi lại cho HCNS" : PAYROLL_ACK_DEFAULT_LABEL}
                </button>
              </>
            ) : null}
          </div>
        ) : null}
        <AttendanceMonthSheetView
          monthValue={monthValue}
          overview={overview}
          loading={loading}
          editable
          draftReasons={draftReasons}
          onDraftChange={(dayKey, patch) =>
            setDraftReasons((prev) => {
              const logs =
                (overview?.logs || []).filter((l) => workDateKey(l.work_date) === dayKey) || [];
              const cur = prev[dayKey] || emptyDraftExplanation(logs);
              return { ...prev, [dayKey]: { ...cur, ...patch } };
            })
          }
          explanationPlaceholder={explanationPlaceholder}
          canExplain={canExplain}
          canConfirmWrite={canConfirmWrite}
          canConfirmDelete={canConfirmDelete}
          hasSavedExplanationOnServer={hasSavedExplanationOnServer}
          savingDay={savingDay}
          deleteKey={deleteKey}
          onSaveExplanation={handleSaveExplanation}
          onDeleteExplanation={handleDeleteExplanation}
        />
      </div>
      ) : null}

      {canHrExplanationsList && confirmSubTab === "hr-review" ? (
        <div
          className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:p-4 md:p-5"
          data-tour="hrm-attendance-confirm-hr-review"
        >
          <div className="mb-4 min-w-0">
            <div className="flex items-start gap-2">
              <div className="shrink-0 rounded-2xl bg-red-50 p-2.5 text-red-700">
                <UserRoundSearch className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-black uppercase tracking-wide text-gray-700">
                  HCNS — Xem phiếu theo nhân viên / Phê duyệt kỳ công
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-gray-500">
                  Chọn chi nhánh và nhân viên để xem phiếu công, rồi Duyệt hoặc Không duyệt phiếu của nhân viên đó.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)_auto_auto] xl:items-end">
            <div className="min-w-0 space-y-1">
              <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500">
                Chi nhánh
              </label>
              <HrmDashboardBranchPicker
                instanceId="hrm-attendance-confirm-branch-review"
                classNamePrefix="hrm-dash-branch-att-confirm-review"
                className="w-full"
                options={branchSelectOptions}
                value={listBranchId}
                onChange={setListBranchId}
                aria-label="Lọc chi nhánh khi xem phiếu nhân viên"
              />
            </div>

            <div className="min-w-0 space-y-1">
              <label className="block text-[11px] font-bold uppercase tracking-wide text-gray-500">
                Nhân viên
              </label>
              <Select
                instanceId="hrm-attendance-confirm-employee-picker"
                classNamePrefix="hrm-att-confirm-emp"
                className="w-full"
                options={hrEmployeeSelectOptions}
                value={selectedHrEmployeeOption}
                onChange={(opt) => setSelectedHrEmployeeId(opt?.value ? String(opt.value) : "")}
                isClearable={false}
                isSearchable
                placeholder={hrEmployees.length ? "Chọn nhân viên..." : "Không có nhân viên trong phạm vi"}
                noOptionsMessage={() => "Không có nhân viên"}
                filterOption={(option, rawInput) => {
                  const q = String(rawInput || "").trim().toLowerCase();
                  if (!q) return true;
                  const name = String(option.data?.label || "").toLowerCase();
                  const sub = String(option.data?.sub || "").toLowerCase();
                  const code = String(option.data?.meta?.employee_code || "").toLowerCase();
                  return name.includes(q) || sub.includes(q) || code.includes(q);
                }}
                components={{ Option: HrEmployeeMenuOption, SingleValue: HrEmployeeSingleValue }}
                styles={hrEmployeeSelectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </div>

            <button
              type="button"
              onClick={() => handleEmployeeReview("approved")}
              disabled={
                reviewingEmployee ||
                !selectedHrEmployeeId ||
                period?.status === "closed" ||
                !period
              }
              title={
                !period
                  ? "Chưa có kỳ bảng công cho tháng này."
                  : period?.status === "closed"
                    ? "Kỳ đã đóng — không thể duyệt thêm."
                    : !selectedHrEmployeeId
                      ? "Chọn nhân viên trước."
                      : undefined
              }
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 text-sm font-bold text-white shadow-sm hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-gray-400 xl:min-w-[8.5rem]"
            >
              {reviewingEmployee ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 shrink-0" strokeWidth={2.2} />
              )}
              Duyệt
            </button>

            <button
              type="button"
              onClick={() => handleEmployeeReview("rejected")}
              disabled={
                reviewingEmployee ||
                !selectedHrEmployeeId ||
                period?.status === "closed" ||
                !period
              }
              title={
                !period
                  ? "Chưa có kỳ bảng công cho tháng này."
                  : period?.status === "closed"
                    ? "Kỳ đã đóng — không thể cập nhật."
                    : !selectedHrEmployeeId
                      ? "Chọn nhân viên trước."
                      : undefined
              }
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 text-sm font-bold text-red-800 shadow-sm hover:bg-red-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 xl:min-w-[8.5rem]"
            >
              {reviewingEmployee ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4 shrink-0" strokeWidth={2.2} />
              )}
              Không duyệt
            </button>
          </div>

          {selectedHrEmployeeMeta || hrEmployeeOverview?.employee ? (
            <div className="mb-3 rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-3 text-sm">
              <p className="font-black uppercase tracking-tight text-gray-900">
                {(
                  hrEmployeeOverview?.employee?.employee_name ||
                  selectedHrEmployeeMeta?.employee_name ||
                  "—"
                ).toString()}
              </p>
              <p className="mt-0.5 text-[11px] font-semibold leading-snug text-gray-500">
                {employeeDeptCodeLine(
                  hrEmployeeOverview?.employee || selectedHrEmployeeMeta || {}
                )}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-semibold">
                {(() => {
                  const reviewStatus =
                    hrEmployeeOverview?.hr_review?.status || selectedHrEmployeeMeta?.hr_review_status;
                  if (reviewStatus === "approved") {
                    return (
                      <span className="rounded-lg bg-emerald-50 px-2 py-0.5 text-emerald-800 ring-1 ring-emerald-100">
                        HCNS đã duyệt
                        {(hrEmployeeOverview?.hr_review?.reviewed_at ||
                          selectedHrEmployeeMeta?.hr_reviewed_at) &&
                          ` · ${formatDateTime(
                            hrEmployeeOverview?.hr_review?.reviewed_at ||
                              selectedHrEmployeeMeta?.hr_reviewed_at
                          )}`}
                      </span>
                    );
                  }
                  if (reviewStatus === "rejected") {
                    const rejectNote =
                      hrEmployeeOverview?.hr_review?.note ||
                      selectedHrEmployeeMeta?.hr_review_note ||
                      "";
                    return (
                      <span className="max-w-full rounded-lg bg-red-50 px-2 py-0.5 text-red-800 ring-1 ring-red-100">
                        HCNS không duyệt
                        {(hrEmployeeOverview?.hr_review?.reviewed_at ||
                          selectedHrEmployeeMeta?.hr_reviewed_at) &&
                          ` · ${formatDateTime(
                            hrEmployeeOverview?.hr_review?.reviewed_at ||
                              selectedHrEmployeeMeta?.hr_reviewed_at
                          )}`}
                        {rejectNote ? (
                          <span className="mt-0.5 block font-medium normal-case tracking-normal">
                            Lý do: {rejectNote}
                          </span>
                        ) : null}
                      </span>
                    );
                  }
                  return (
                    <span className="rounded-lg bg-white px-2 py-0.5 text-gray-600 ring-1 ring-gray-200">
                      Chưa duyệt bởi HCNS
                    </span>
                  );
                })()}
                {hrEmployeeOverview?.payroll_agreement || selectedHrEmployeeMeta?.has_payroll_agreement ? (
                  <span className="rounded-lg bg-emerald-50 px-2 py-0.5 text-emerald-800 ring-1 ring-emerald-100">
                    Đã đồng ý bảng công
                    {hrEmployeeOverview?.payroll_agreement?.acknowledged_at
                      ? ` · ${formatDateTime(hrEmployeeOverview.payroll_agreement.acknowledged_at)}`
                      : ""}
                  </span>
                ) : (
                  <span className="rounded-lg bg-amber-50 px-2 py-0.5 text-amber-900 ring-1 ring-amber-100">
                    Chưa đồng ý bảng công
                  </span>
                )}
                <span className="rounded-lg bg-white px-2 py-0.5 text-gray-600 ring-1 ring-gray-200">
                  {(hrEmployeeOverview?.explanations || []).length ||
                    Number(selectedHrEmployeeMeta?.explanation_count || 0)}{" "}
                  giải trình
                </span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-gray-200/80 pt-3 sm:grid-cols-3 lg:grid-cols-5">
                <div className="rounded-xl border border-gray-100 bg-white px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    Tổng ngày công
                  </p>
                  <p className="mt-1 text-lg font-black tabular-nums text-gray-900">
                    {loadingHrEmployee && !hrEmployeeOverview ? "—" : hrEmployeeMonthStats.workDays}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    Tổng giờ công
                  </p>
                  <p className="mt-1 text-lg font-black tabular-nums text-gray-900">
                    {loadingHrEmployee && !hrEmployeeOverview ? (
                      "—"
                    ) : (
                      <>
                        {hrEmployeeMonthStats.workHoursLabel}
                        <span className="ml-0.5 text-xs font-bold text-gray-400">giờ</span>
                      </>
                    )}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    Tổng giờ tăng ca
                  </p>
                  <p className="mt-1 text-lg font-black tabular-nums text-gray-900">
                    {loadingHrEmployee && !hrEmployeeOverview ? (
                      "—"
                    ) : (
                      <>
                        {hrEmployeeMonthStats.otLabel}
                        <span className="ml-0.5 text-xs font-bold text-gray-400">giờ</span>
                      </>
                    )}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    Tổng ngày nghỉ phép
                  </p>
                  <p className="mt-1 text-lg font-black tabular-nums text-gray-900">
                    {loadingHrEmployee && !hrEmployeeOverview ? "—" : hrEmployeeMonthStats.leaveDays}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-100 bg-white px-3 py-2.5">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">
                    Số lượng giải trình
                  </p>
                  <p className="mt-1 text-lg font-black tabular-nums text-gray-900">
                    {loadingHrEmployee && !hrEmployeeOverview
                      ? "—"
                      : hrEmployeeMonthStats.explanationCount}
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {hrEmployees.length === 0 && !loading ? (
            <p className="text-sm font-semibold text-gray-400">
              Không có nhân viên có dữ liệu chấm công / nghỉ phép / tăng ca trong tháng và phạm vi đã chọn.
            </p>
          ) : (
            <AttendanceMonthSheetView
              monthValue={monthValue}
              overview={hrEmployeeOverview}
              loading={loadingHrEmployee}
              editable={false}
              draftReasons={hrEmployeeDraftReasons}
              highlightExplanations
              allowHrReview={canHrExplanationsList}
              allowHrDelete={canConfirmDelete}
              canConfirmDelete={canConfirmDelete}
              hasSavedExplanationOnServer={
                new Set(Object.keys(hrEmployeeDraftReasons || {}))
              }
              deleteKey={deleteKey}
              deleteKeyPrefix={`hr:${selectedHrEmployeeId}`}
              reviewingDay={reviewingExplanationDay}
              onReviewExplanation={(dayKey, status) =>
                handleHrExplanationReview(selectedHrEmployeeId, dayKey, status, [
                  () => loadHrEmployeeOverview(selectedHrEmployeeId),
                ])
              }
              onDeleteExplanation={async (dayKey) => {
                const deleted = await handleDeleteHrExplanation({
                  employee_id: selectedHrEmployeeId,
                  work_date: dayKey,
                  employee_name:
                    hrEmployeeOverview?.employee?.employee_name ||
                    selectedHrEmployeeMeta?.employee_name ||
                    "",
                });
                if (deleted) await loadHrEmployeeOverview(selectedHrEmployeeId);
              }}
              emptyHint="Chọn nhân viên để xem phiếu chấm công trong tháng."
            />
          )}
        </div>
      ) : null}

      {canHrExplanationsList && confirmSubTab === "hr-confirmations" ? (
        <div className="rounded-2xl border border-gray-100 bg-white p-3 shadow-sm sm:p-4 md:p-5">
          <div className="mb-3 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h3 className="min-w-0 shrink text-sm font-black uppercase leading-tight tracking-wide text-gray-700">
                Xác nhận từ nhân viên (HCNS)
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-gray-500">
                Danh sách nhân viên đã đồng ý tổng bảng công. Bấm vào dòng để xem giải trình theo ngày (nếu có).
              </p>
            </div>
            <div className="w-full min-w-0 sm:w-auto sm:max-w-[20rem]">
              <label className="mb-1 block text-[11px] font-bold text-gray-500 sm:hidden">
                Lọc chi nhánh
              </label>
              <HrmDashboardBranchPicker
                instanceId="hrm-attendance-confirm-branch-list"
                classNamePrefix="hrm-dash-branch-att-confirm-list"
                className="w-full"
                options={branchSelectOptions}
                value={listBranchId}
                onChange={setListBranchId}
                aria-label="Lọc chi nhánh: danh sách đồng ý bảng công"
              />
            </div>
          </div>

          <p className="mb-2 text-[11px] font-black uppercase tracking-wide text-gray-500">
            Xác nhận đồng ý tổng (có thể kèm giải trình theo ngày)
          </p>
          {hrPayrollAgreements.length === 0 ? (
            <p className="text-sm font-semibold text-gray-400">
              Chưa có nhân viên nào gửi xác nhận tổng trong tháng / lọc chi nhánh đã chọn.
            </p>
          ) : (
            <>
              <div className="touch-pan-y space-y-3 md:hidden">
                {hrPayrollAgreements.map((row, idx) => {
                  const exCount = explanationCountByEmployee.get(String(row.employee_id)) || 0;
                  return (
                    <button
                      key={`${row.employee_id}-ack-${idx}`}
                      type="button"
                      onClick={() => setHrAckDetail(row)}
                      className="w-full rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 text-left transition hover:border-emerald-300 hover:bg-emerald-50"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-black uppercase tracking-tight text-gray-900">
                            {row.employee_name}
                          </p>
                          <p className="mt-0.5 text-[11px] italic leading-snug text-gray-500">
                            {hrExplanationBranchDeptLine(row)}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-lg bg-white px-2 py-0.5 text-[11px] font-bold text-gray-700 ring-1 ring-gray-200">
                          {exCount} giải trình
                        </span>
                      </div>
                      <p className="mt-2 text-[11px] font-semibold text-gray-600">
                        Thời điểm gửi: {formatDateTime(row.acknowledged_at)}
                      </p>
                      <p className="mt-1 break-words text-sm text-gray-800">
                        {row.message || PAYROLL_ACK_DEFAULT_LABEL}
                      </p>
                    </button>
                  );
                })}
              </div>
              <div className="hidden md:block md:overflow-x-auto">
                <table className="min-w-[640px] w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-[11px] font-black uppercase text-gray-400">
                      <th className="py-2 pr-4">Nhân viên</th>
                      <th className="py-2 pr-4">Thời điểm gửi</th>
                      <th className="py-2 pr-4">Nội dung</th>
                      <th className="py-2 pr-4 whitespace-nowrap">Giải trình</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hrPayrollAgreements.map((row, idx) => {
                      const exCount = explanationCountByEmployee.get(String(row.employee_id)) || 0;
                      return (
                        <tr
                          key={`${row.employee_id}-ack-${idx}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => setHrAckDetail(row)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setHrAckDetail(row);
                            }
                          }}
                          className="cursor-pointer border-b border-gray-50 transition hover:bg-emerald-50/60"
                        >
                          <td className="py-2 pr-4 align-top">
                            <div className="font-black uppercase tracking-tight text-gray-900">
                              {row.employee_name}
                            </div>
                            <div className="mt-0.5 text-[11px] italic leading-snug text-gray-500">
                              {hrExplanationBranchDeptLine(row)}
                            </div>
                          </td>
                          <td className="whitespace-nowrap py-2 pr-4 align-top text-gray-700">
                            {formatDateTime(row.acknowledged_at)}
                          </td>
                          <td className="max-w-[24rem] break-words py-2 text-gray-700">
                            {row.message || PAYROLL_ACK_DEFAULT_LABEL}
                          </td>
                          <td className="whitespace-nowrap py-2 align-top">
                            <span
                              className={`inline-flex rounded-lg px-2 py-0.5 text-[11px] font-bold ring-1 ${
                                exCount > 0
                                  ? "bg-amber-50 text-amber-900 ring-amber-100"
                                  : "bg-gray-50 text-gray-500 ring-gray-200"
                              }`}
                            >
                              {exCount}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {hrAckDetail ? (
            <div
              className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-900/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="hr-ack-detail-title"
              onClick={closeHrAckDetail}
            >
              <div
                className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-t-2xl border border-gray-100 bg-white shadow-2xl sm:rounded-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="shrink-0 border-b border-gray-100 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40 px-4 py-4 sm:px-6 sm:py-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p
                        id="hr-ack-detail-title"
                        className="text-[11px] font-black uppercase tracking-[0.14em] text-emerald-800"
                      >
                        Phiếu công & giải trình theo ngày
                      </p>
                      <p className="mt-1 text-base font-black uppercase tracking-tight text-gray-900 sm:text-lg">
                        {hrAckDetail.employee_name}
                      </p>
                      <p className="mt-0.5 text-[11px] italic text-gray-500">
                        {hrExplanationBranchDeptLine(
                          hrAckDetailOverview?.employee || hrAckDetail
                        )}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-100/90 px-2.5 py-1 text-[11px] font-bold text-emerald-900 ring-1 ring-emerald-200">
                          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.4} />
                          Đã đồng ý tổng
                          {hrAckDetail.acknowledged_at
                            ? ` · ${formatDateTime(hrAckDetail.acknowledged_at)}`
                            : ""}
                        </span>
                        <span className="inline-flex rounded-lg bg-white px-2.5 py-1 text-[11px] font-bold text-gray-700 ring-1 ring-gray-200">
                          Tháng {monthValue}
                        </span>
                        <span
                          className={`inline-flex rounded-lg px-2.5 py-1 text-[11px] font-bold ring-1 ${
                            hrAckDetailSavedSet.size > 0
                              ? "bg-amber-50 text-amber-950 ring-amber-200"
                              : "bg-white text-gray-500 ring-gray-200"
                          }`}
                        >
                          {hrAckDetailSavedSet.size} ngày có giải trình
                        </span>
                      </div>
                      <p className="mt-2 text-xs leading-relaxed text-gray-600">
                        {hrAckDetail.message || PAYROLL_ACK_DEFAULT_LABEL}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeHrAckDetail}
                      aria-label="Đóng"
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
                    >
                      <X className="h-5 w-5" strokeWidth={2.2} />
                    </button>
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-6">
                  <p className="mb-3 text-[11px] font-semibold leading-relaxed text-gray-500">
                    Ngày có giải trình được tô nền vàng. Có thể xóa giải trình trực tiếp trên từng dòng nếu bạn có
                    quyền.
                  </p>
                  <AttendanceMonthSheetView
                    monthValue={monthValue}
                    overview={hrAckDetailOverview}
                    loading={loadingHrAckDetail}
                    editable={false}
                    draftReasons={hrAckDetailDraftReasons}
                    highlightExplanations
                    allowHrDelete
                    allowHrReview={canHrExplanationsList}
                    canConfirmDelete={canConfirmDelete}
                    hasSavedExplanationOnServer={hrAckDetailSavedSet}
                    deleteKey={deleteKey}
                    deleteKeyPrefix={`hr:${hrAckDetail.employee_id}`}
                    reviewingDay={reviewingExplanationDay}
                    onReviewExplanation={(dayKey, status) =>
                      handleHrExplanationReview(hrAckDetail.employee_id, dayKey, status, [
                        async () => {
                          try {
                            const data = await getAttendanceConfirmationEmployeeOverview({
                              year_month: monthValue,
                              employee_id: hrAckDetail.employee_id,
                            });
                            setHrAckDetailOverview(data);
                          } catch (e) {
                            console.error(e);
                          }
                        },
                      ])
                    }
                    onDeleteExplanation={handleDeleteHrExplanationByDay}
                    emptyHint="Không tải được phiếu công của nhân viên này."
                  />
                </div>

                <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/80 px-4 py-3 sm:px-6">
                  <button
                    type="button"
                    onClick={closeHrAckDetail}
                    className="inline-flex h-11 w-full items-center justify-center rounded-xl border border-gray-300 bg-white px-5 text-sm font-bold text-gray-800 hover:bg-white sm:w-auto"
                  >
                    Đóng
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default AttendanceConfirmationTab;
